/*
 * @Author: lizhigang
 * @Date: 2025-10-22 14:53:33
 * @Company: www.smm.cn
 * @Description: 注册
 */
"use client"

import IconFont from "@repo/ui/iconfont"
import http from "@repo/ui/utils"
import classNames from "classnames"
import md5 from "md5"
import { useLocale, useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import { Button, Form, Input, message, Space } from "antd"

import { useFetchCode, useMessage, usePasswordInput } from "../../_hooks"

import "./index.scss"

import { setClientCookie } from "@repo/shared"

import { COOKIE_TEMPORARY_KEY, SIGN_IN_EN_H5_SOURCE_MAP, SIGN_IN_EN_WEB_SOURCE_MAP } from "../../const"
import { isMobile } from "../../utils"
import { AuthConfigProvider } from "../AuthConfigProvider"
import { createPasswordValidator, PasswordRulesPopover } from "../PasswordRulesPopover"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  schema?: "page" | "modal"
  language?: string
  onRegistered?: (msg?: string) => void
  onClose?: () => void
  handleSignIn: () => void
}

const Register = ({ schema = "modal", language, onRegistered, onClose, handleSignIn, ...attr }: Props) => {
  const locale = useLocale()
  const t = useTranslations()
  const tr = (key: string) => t(`register.${key}`)
  const [form] = Form.useForm()
  const [account, setAccount] = useState("")
  const [verifyCode, setVerifyCode] = useState("")
  const [password, setPassword] = useState("")
  const [hasPasswordError, setHasPasswordError] = useState(false)
  const [isH5, setIsH5] = useState(false)
  const [loading, setLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage({
    getContainer: () => document.querySelector("#action") || document.body,
  })

  // 使用 message hook，从 App.useApp() 获取 message 实例
  const { showSuccess, showError } = useMessage({ schema })

  const { fetchCode, count, sent } = useFetchCode({ account, form, schema })

  // 使用密码输入 Hook
  const { handlePasswordChange, handlePasswordPaste, handleCompositionStart } = usePasswordInput({
    form,
    fieldName: "password",
    onPasswordChange: setPassword,
  })

  const handleFieldsChange = (_changedFields: unknown[], allFields: any[]) => {
    const passwordField = allFields.find((field) => field.name?.[0] === "password")
    setHasPasswordError((passwordField?.errors?.length ?? 0) > 0)
  }

  useEffect(() => {
    setIsH5(isMobile())
  }, [])

  /**
   * 注册
   */
  const handleSubmit = async () => {
    const values = await form.validateFields()
    try {
      if (values) {
        const formData = new FormData()
        formData.append("user_name", values.user_name.trim())
        formData.append("password", md5(values.password))
        formData.append("verify_code", verifyCode.trim())
        formData.append(
          "source",
          isMobile()
            ? (SIGN_IN_EN_H5_SOURCE_MAP[window.location.host] ?? "")
            : (SIGN_IN_EN_WEB_SOURCE_MAP[window.location.host] ?? "")
        )
        formData.append("referrer", window.location.href || "")
        formData.append("user_agent", navigator.userAgent || "")
        setLoading(true)
        const response = await http.post<
          FormData,
          {
            token: string
            user_info: { cellphone: string; company_id: number; email: string; nickname: string; user_id: number }
          }
        >(`/usercenter/english/v1/register`, {
          body: formData,
          showOriginResponse: true,
          getLang: () => locale,
        })
        setLoading(false)
        if (response?.code === 0) {
          const { data } = response
          schema === "modal"
            ? showSuccess(tr("registerSuccess"), {
                duration: 1,
                onClose: async () => {
                  setClientCookie(COOKIE_TEMPORARY_KEY, data!.token, undefined, ".metal.com")
                  if (schema === "modal") {
                    onClose?.()
                  }
                  onRegistered?.(tr("registerSuccess"))
                },
              })
            : messageApi.open({
                type: "success",
                content: tr("registerSuccess"),
                duration: 1,
                onClose: async () => {
                  setClientCookie(COOKIE_TEMPORARY_KEY, data!.token, undefined, ".metal.com")
                  onRegistered?.(tr("registerSuccess"))
                },
              })
        } else {
          throw new Error(response?.msg)
        }
      }
    } catch (errorInfo) {
      const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
      schema === "modal"
        ? showError(msg || "unknown error")
        : messageApi.open({
            type: "error",
            content: msg || "unknown error",
          })
    }
  }

  return (
    <AuthConfigProvider>
      {contextHolder}
      <div className={classNames("registerWrapper", { page: schema === "page", modal: schema === "modal" })} {...attr}>
        <div className="logo">
          <IconFont type="icon-SMMlogo-daizimu" style={{ width: 80, height: 32, fontSize: 80 }} />
        </div>
        <h2 className={classNames("modalTitle", { page: schema === "page", modal: schema === "modal" })}>
          {tr("title")}
        </h2>
        {/*<ConfigProvider*/}
        {/*  theme={{*/}
        {/*    components: {*/}
        {/*      Form: {*/}
        {/*        verticalLabelMargin: 40,*/}
        {/*      },*/}
        {/*    },*/}
        {/*  }}*/}
        {/*>*/}
        <Form
          layout="vertical"
          form={form}
          variant="filled"
          requiredMark={false}
          initialValues={{ variant: "borderless" }}
          className={"registerForm"}
          onFieldsChange={handleFieldsChange}
        >
          <Form.Item
            name="user_name"
            label={schema === "page" ? null : tr("emailAddress")}
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: tr("requireEmail") },
              { type: "email", message: tr("validEmail") },
            ]}
          >
            <Input
              placeholder={tr("emailAddress")}
              onChange={(e) => setAccount(e.target.value)}
              onPressEnter={fetchCode}
              allowClear
              autoFocus
            />
          </Form.Item>
          <Form.Item
            label={schema === "page" ? null : tr("emailCode")}
            normalize={(value) => value?.trim()}
            rules={[{ required: true, message: tr("requireCode") }]}
          >
            <Space.Compact block>
              <Input
                placeholder={tr("emailCode")}
                onChange={(e) => setVerifyCode(e.target.value)}
                onPressEnter={handleSubmit}
                allowClear
              />
              <span className="sendButton">
                <Button disabled={!account || (count > 0 && count < 60)} onClick={fetchCode}>
                  {count > 0 && count < 60 ? `${tr("resend")}(${count}${tr("s")})` : sent ? tr("resend") : tr("send")}
                </Button>
              </span>
            </Space.Compact>
          </Form.Item>
          <Form.Item className="password-wrapper" style={{ position: "relative" }}>
            <Form.Item
              name="password"
              label={schema === "page" ? null : tr("password")}
              normalize={(value) => value?.trim()}
              rules={[{ required: true, message: tr("requirePassword") }, { validator: createPasswordValidator(t) }]}
              required={false}
            >
              <PasswordRulesPopover>
                <Input.Password
                  placeholder={tr("password")}
                  onChange={handlePasswordChange}
                  onPressEnter={handleSubmit}
                  onCompositionStart={handleCompositionStart}
                  onPaste={handlePasswordPaste}
                  allowClear
                />
              </PasswordRulesPopover>
            </Form.Item>
            {isH5 && !hasPasswordError && <div className="simplePasswordTip">{t("forgot.simplePasswordTip")}</div>}
          </Form.Item>
          <Form.Item className="submitButton">
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!account.trim() || !password.trim() || !verifyCode.trim()}
              loading={loading}
              block
              danger
            >
              {tr("continue")}
            </Button>
          </Form.Item>
        </Form>
        {/*</ConfigProvider>*/}
        <div className={"bottomTip"}>
          {tr("hasAccount")}
          <a className={"text"} onClick={handleSignIn}>
            {tr("signIn")}
          </a>
        </div>
      </div>
    </AuthConfigProvider>
  )
}

export default Register
